import type { TFunction } from 'i18next';

import type { FeedingPen, PenWarning } from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { confIntervalTone, fallthroughTone } from '../feedingChartColors';
import { formatPercent } from '../percentFormat';
import type { SystemLabels } from '../systemLabel';

import { format, sourceTitle } from './penComparisonFormat';
import { infoCellClass, pelletCellClass } from './penComparisonTones';
import { joinTitles } from './penInfoTitle';
import { penRowCells } from './penRowCells';

/** The body columns after the pen, in the header's order (U10). */
export type PenFigureColumn =
    | 'fishCount'
    | 'weightPlan'
    | 'weightActual'
    | 'weightBioscope'
    | 'weightInterval'
    | 'fcrPms'
    | 'fcrBioscope'
    | 'fcrInterval'
    | 'sfrPms'
    | 'sfrBioscope'
    | 'rgiFeed'
    | 'rgiBioscope'
    | 'feedVsPlan'
    | 'pellet';

/**
 * One figure cell. A cell with an information `band` and a title draws its figure as a
 * focusable button, so the band's explanation reaches keyboard and touch users too; any
 * other cell keeps its title as a plain tooltip.
 */
export interface PenFigureCell {
    figure: string;
    title: string | undefined;
    /** The warning or breach class, which the cell draws whether or not it is banded. */
    className?: string;
    /** The information mark of a band cell. */
    band?: string;
}

/** One pen's row: its label, its warnings and every figure cell. */
export interface PenComparisonRowData {
    pen: FeedingPen;
    name: string;
    warnings: PenWarning[];
    cells: Record<PenFigureColumn, PenFigureCell>;
}

/** Works out every cell of one pen's row, once, so the columns only read it. */
export function penComparisonRowData(
    pen: FeedingPen,
    name: string,
    system: SystemLabels,
    t: TFunction,
    locale: Locale
): PenComparisonRowData {
    const {
        warnings,
        collecting,
        breach,
        sfr,
        unmapped,
        bioscopeInfo,
        info,
        bioscopeInfoTitle,
        reason,
        feedVsPlan,
    } = penRowCells(pen, system, t, locale);
    // IPS 35 fixes the denominator as Weight actual × fish count; naming it on the cell
    // says why the rate can differ from one a provider computes over its own biomass.
    const sfrPmsTitle = pen.sfrPmsPct == null ? undefined : system.sfrBiomass;
    const confRatio =
        pen.fcrBioscopeConfInterval != null && pen.fcrBioscope
            ? pen.fcrBioscopeConfInterval / pen.fcrBioscope
            : null;
    // Pellet counts come from the feeding camera, not the weight estimates, so the
    // pellet cell follows pellet availability and ignores the weight startup window.
    const showFallthrough = pen.fallthroughAvailable;

    return {
        pen,
        name,
        warnings,
        cells: {
            // Planning-system data, so shown whatever the Bioscope state (IPS 6).
            fishCount: {
                figure: format(pen.fishCount, 0, locale),
                title: reason('fishCount', pen.fishCount) ?? unmapped(pen.dataSources.fishCount),
            },
            weightPlan: {
                figure: format(pen.weightPmsPlanG, 0, locale),
                title:
                    reason('weightPmsPlan', pen.weightPmsPlanG) ??
                    unmapped(pen.dataSources.weightPmsPlan),
            },
            weightActual: {
                figure: format(pen.weightPmsActualG, 0, locale),
                title:
                    reason('weightPmsActual', pen.weightPmsActualG) ??
                    unmapped(pen.dataSources.weightPmsActual),
            },
            weightBioscope: {
                figure: collecting ? '–' : format(pen.weightBioscopeG, 0, locale),
                title: reason('weightBioscope', pen.weightBioscopeG, true),
            },
            weightInterval: {
                figure:
                    collecting || pen.weightBioscopeConfIntervalG == null
                        ? '–'
                        : `±${format(pen.weightBioscopeConfIntervalG, 0, locale)}`,
                title: reason('weightBioscope', pen.weightBioscopeConfIntervalG, true),
            },
            fcrPms: {
                figure: format(pen.fcrPms, 2, locale),
                title: reason('fcrPms', pen.fcrPms) ?? unmapped(pen.dataSources.fcrPms),
                className: breach('fcrSystem'),
            },
            fcrBioscope: {
                figure: collecting ? '–' : format(pen.fcrBioscope, 2, locale),
                title: reason('fcrBioscope', pen.fcrBioscope, true),
                className: breach('fcrBioscope'),
            },
            fcrInterval: {
                figure:
                    collecting || pen.fcrBioscopeConfInterval == null
                        ? '–'
                        : `±${format(pen.fcrBioscopeConfInterval, 3, locale)}`,
                title:
                    reason('fcrBioscope', pen.fcrBioscopeConfInterval, true) ??
                    bioscopeInfoTitle({ kind: 'interval', ratio: confRatio, config: pen.config }),
                band: bioscopeInfo(
                    confIntervalTone(
                        confRatio,
                        pen.config.confIntervalGoodPct,
                        pen.config.confIntervalWarnPct
                    )
                ),
            },
            // SFR carries an information mark against the healthy range but is never a
            // warning trigger: the spec's "no highlighting" was confirmed (2026-08-17) to
            // mean the pen is never flagged for it, while the mark is what makes an
            // out-of-range rate visible, so it stays.
            sfrPms: {
                figure: formatPercent(pen.sfrPmsPct, 2, { locale }),
                title:
                    reason('sfrPms', pen.sfrPmsPct) ??
                    joinTitles(
                        info({ kind: 'sfr', pct: pen.sfrPmsPct, config: pen.config }),
                        sfrPmsTitle
                    ),
                band: infoCellClass(sfr(pen.sfrPmsPct)),
            },
            sfrBioscope: {
                figure: collecting ? '–' : formatPercent(pen.sfrBioscopePct, 2, { locale }),
                title:
                    reason('sfrBioscope', pen.sfrBioscopePct, true) ??
                    bioscopeInfoTitle({ kind: 'sfr', pct: pen.sfrBioscopePct, config: pen.config }),
                band: bioscopeInfo(sfr(pen.sfrBioscopePct)),
            },
            rgiFeed: {
                figure: format(pen.rgiFeed, 1, locale),
                title: reason('rgiFeed', pen.rgiFeed),
                className: breach('rgiSystem'),
            },
            rgiBioscope: {
                figure: collecting ? '–' : format(pen.rgiBioscope, 1, locale),
                title: reason('rgiBioscope', pen.rgiBioscope, true),
                className: breach('rgiBioscope'),
            },
            feedVsPlan: {
                figure: formatPercent(pen.feedVsPlanPct, 0, { locale, signed: true }),
                title: feedVsPlan.title,
                band: feedVsPlan.className,
            },
            pellet: {
                figure: showFallthrough ? formatPercent(pen.fallthroughPct, 1, { locale }) : '–',
                // The column is present whenever ANY pen on the site has 3.0 hardware, so a
                // 2.0 pen's missing camera has to be distinguishable from a 3.0 pen's empty
                // period.
                title:
                    sourceTitle(pen.dataSources.pellet, t('feedingModule.table.pelletNoCamera')) ??
                    (showFallthrough
                        ? info({ kind: 'pellet', pct: pen.fallthroughPct, config: pen.config })
                        : undefined),
                band: showFallthrough
                    ? pelletCellClass(
                          fallthroughTone(
                              pen.fallthroughPct,
                              pen.config.fallthroughWarnPct,
                              pen.config.fallthroughAlertPct
                          )
                      )
                    : undefined,
            },
        },
    };
}
